import { conversationDetailQueryKey } from "@/api/chat/chat";
import { Button } from "@/components/ui/button";
import { TeamVisibleBadge } from "@/components/shared/TeamVisibleBadge";
import { TruncatedTooltip } from "@/components/shared/TruncatedTooltip";
import { useCachedConversationDetail } from "@/hooks/useConversationDetail";
import { useSharedConversation } from "@/hooks/useSharedConversation";
import {
    ThreadListItemMorePrimitive,
    ThreadListItemPrimitive,
    useAuiState,
} from "@assistant-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { MoreHorizontalIcon, TrashIcon } from "lucide-react";
import type { FC } from "react";
import { Link, useLocation, useNavigate } from "react-router";

// Styling decisions live in `.impeccable.md` → "Thread list rows". The
// container classes mirror the owned `ThreadListItem` in
// `thread-list.tsx` except for:
//   - `h-9` → `h-auto` so two lines can fit
//   - Trigger is `flex-col` (two-line stack) instead of `flex-row`
//   - `py-1.5` vertical padding on the Trigger (owned row has no vertical
//     padding because it's a single-line 36px row)
// Remove wires through `ThreadListItemPrimitive.Delete` into the adapter so
// assistant-ui fires optimistic list-removal + rollback. An inline mutation
// would invalidate `["sharedConversations"]` (refreshing the subtitle) but
// the row would stay in the sidebar until the next `list()` call.
export const SharedThreadListItem: FC = () => {
    const remoteId = useAuiState(
        ({ threadListItem }) => threadListItem?.remoteId
    );
    const runtimeTitle = useAuiState(
        ({ threadListItem }) => threadListItem?.title ?? ""
    );
    const item = useSharedConversation(remoteId);
    const detail = useCachedConversationDetail(remoteId);
    const title =
        runtimeTitle || detail?.title || item?.conversation_title || "";

    const sharedAgo = item
        ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true })
        : null;

    return (
        <ThreadListItemPrimitive.Root className="aui-thread-list-item aui-thread-list-item--shared group hover:bg-muted focus-visible:bg-muted data-active:bg-muted relative flex h-auto items-stretch rounded-lg transition-colors focus-visible:outline-none motion-reduce:transition-none">
            <ThreadListItemPrimitive.Trigger
                className="aui-thread-list-item-trigger flex min-w-0 flex-1 flex-col items-start gap-0.5 py-1.5 pr-10 pl-3 text-start"
                render={
                    <Link
                        to={
                            remoteId
                                ? `/organizations/chat/${remoteId}`
                                : "/organizations/chat"
                        }
                    />
                }
            >
                <TruncatedTooltip
                    text={title}
                    className="aui-thread-list-item-title max-w-full min-w-0 text-sm"
                    tooltipSide="right"
                />
                {/* See OwnedThreadListItem for why the primitive's Title is
                    swapped out — same reasoning: the same `title` value lets
                    TruncatedTooltip measure overflow and decide whether to
                    mount a tooltip on hover. */}
                {item ? (
                    // VS7 (R10): a team-visible row reads as the shared "Team"
                    // badge instead of "Shared by {name}" — team-visibility
                    // wins over the sharer attribution, in the same DOM slot.
                    item.is_team_visible ? (
                        <span className="aui-thread-list-item-subtitle text-muted-foreground flex max-w-full min-w-0 items-center gap-1 text-xs">
                            <TeamVisibleBadge />
                            <span aria-hidden>·</span>
                            <span className="tabular-nums">{sharedAgo}</span>
                        </span>
                    ) : (
                        <span className="aui-thread-list-item-subtitle text-muted-foreground max-w-full min-w-0 truncate text-xs">
                            Shared by {item.shared_by_display_name}
                            {" · "}
                            <span className="tabular-nums">{sharedAgo}</span>
                        </span>
                    )
                ) : (
                    // Cold-cache placeholder — em-dash, per .impeccable.md
                    // "Thread list rows" → Cold-cache state. aria-hidden
                    // because the link's accessible name is the title;
                    // announcing a decorative em-dash is noise.
                    <span
                        aria-hidden="true"
                        className="aui-thread-list-item-subtitle text-muted-foreground max-w-full min-w-0 truncate text-xs"
                    >
                        {"—"}
                    </span>
                )}
            </ThreadListItemPrimitive.Trigger>

            {/* A team-visible-ONLY row (id === null) reaches the viewer via
                org team-visibility and has no share grant to remove, so the
                unshare affordance is suppressed (bug insurescope-0j62f).
                Explicit-share and dual rows keep a real id and the menu. */}
            {item && remoteId && item.id !== null ? (
                <SharedThreadListItemMore remoteId={remoteId} />
            ) : null}
        </ThreadListItemPrimitive.Root>
    );
};

// "Remove" (not "Delete") because the underlying ChatConversation stays
// owned by the original sharer; only the collaborator's SharedConversation
// grant row goes away. If the viewer is currently on that chat's URL,
// navigate to the empty chat page so they don't land on the 403 that the
// server will return once the grant is gone.
const SharedThreadListItemMore: FC<{ remoteId: string }> = ({ remoteId }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const handleRemoveClick = () => {
        // Drop any cached owner-shaped detail for this conversation so a
        // re-navigation within the conversations query's staleTime can't
        // flash stale data before the server's 404 lands.
        queryClient.removeQueries({
            queryKey: conversationDetailQueryKey(remoteId),
        });
        if (location.pathname === `/organizations/chat/${remoteId}`) {
            navigate("/organizations/chat");
        }
    };

    return (
        <ThreadListItemMorePrimitive.Root>
            <ThreadListItemMorePrimitive.Trigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="aui-thread-list-item-more text-muted-foreground hover:text-foreground data-[state=open]:bg-accent data-[state=open]:text-foreground absolute top-1/2 right-1.5 size-7 -translate-y-1/2 p-0 transition-colors"
                >
                    <MoreHorizontalIcon className="size-4" />
                    <span className="sr-only">More options</span>
                </Button>
            </ThreadListItemMorePrimitive.Trigger>
            <ThreadListItemMorePrimitive.Content
                side="bottom"
                align="start"
                className="aui-thread-list-item-more-content bg-popover text-popover-foreground z-50 min-w-32 overflow-hidden rounded-md border p-1 shadow-md"
            >
                <ThreadListItemPrimitive.Delete
                    render={
                        <ThreadListItemMorePrimitive.Item
                            className="aui-thread-list-item-more-item text-destructive hover:bg-destructive/10 hover:text-destructive focus:bg-destructive/10 focus:text-destructive flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none"
                            onClick={handleRemoveClick}
                        />
                    }
                >
                    <TrashIcon className="size-4" />
                    Remove
                </ThreadListItemPrimitive.Delete>
            </ThreadListItemMorePrimitive.Content>
        </ThreadListItemMorePrimitive.Root>
    );
};
